import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { isPast } from 'date-fns';

import { AmountWithConversion } from '@sorare/core/src/components/buyActions/AmountWithConversion';
import { SorareUser } from '@sorare/core/src/components/user/SorareUser';

import {
  ButtonContainer,
  TokenDetailsRoot,
  TokenDetailsRow,
} from 'components/ItemPreview/ui';
import { AuctionStatus } from 'components/auction/AuctionStatus';
import { AuctionsActions } from 'components/auction/AuctionsActions';
import { useAuctionCurrentPrice } from 'lib/auctions';

import { AuctionWinner } from './AuctionWinner';
import type { AuctionDetails_auction } from './__generated__/index.graphql';

type Props = {
  auction: AuctionDetails_auction;
  isDesktopLayout: boolean;
  hideSorareUser?: boolean;
  useConversionRate?: boolean;
};

export const AuctionDetails = ({
  auction,
  isDesktopLayout,
  hideSorareUser,
  useConversionRate = false,
}: Props) => {
  const currentPriceMonetary = useAuctionCurrentPrice(auction);
  const { endDate, bestBid } = auction;
  const hasEnded = isPast(endDate);

  return (
    <TokenDetailsRoot>
      <div>
        <TokenDetailsRow>
          <AmountWithConversion
            monetaryAmount={
              !useConversionRate && hasEnded && bestBid
                ? bestBid.amounts
                : currentPriceMonetary
            }
          />
        </TokenDetailsRow>
        <TokenDetailsRow>
          {hasEnded ? (
            <AuctionWinner auction={auction} />
          ) : (
            <AuctionStatus auction={auction} />
          )}
        </TokenDetailsRow>
      </div>
      {!!isDesktopLayout && !hideSorareUser && (
        <TokenDetailsRow>
          <SorareUser />
        </TokenDetailsRow>
      )}
      {!hasEnded && (
        <ButtonContainer $isDesktopLayout={isDesktopLayout}>
          <AuctionsActions auction={auction} />
        </ButtonContainer>
      )}
    </TokenDetailsRoot>
  );
};

AuctionDetails.fragments = {
  auction: gql`
    fragment AuctionDetails_auction on TokenAuction {
      id
      open
      bestBid {
        id
        amounts {
          ...AmountWithConversion_monetaryAmount
        }
      }
      ...AuctionStatus_auction
      ...useAuctionCurrentPrice_auction
      ...AuctionWinner_auction
      ...AuctionsActions_auction
    }
    ${AmountWithConversion.fragments.monetaryAmount}
    ${useAuctionCurrentPrice.fragments.auction}
    ${AuctionStatus.fragments.auction}
    ${AuctionWinner.fragments.auction}
    ${AuctionsActions.fragments.auction}
  ` as TypedDocumentNode<AuctionDetails_auction>,
};
